import { computed, shallowRef, triggerRef, type Component, type ComputedRef } from 'vue';

import { onScopeDisposeOrWarn } from '@platform/vue/on-scope-dispose-or-warn';

/**
 * A pluggable indicator rendered on the DevTools toggle pill — a compact,
 * always-visible status badge (counts, countdowns, health dots) shown next to
 * the "Dev Tools" label, even while the panel is collapsed. Any layer that
 * imports `@abax/platform` registers one; `@abax/platform` never imports the
 * contributor. The indicator component owns its own data and may call
 * `useDevToolsPanel().openSection(...)` to deep-link into a section on click.
 */
export interface DevToolsIndicator {
  /** Stable identity. Re-registering the same id replaces the previous entry. */
  id: string;
  /** The badge component to mount on the pill. */
  component: Component;
  /** Sort weight (ascending). Ties break by id. */
  order?: number;
}

interface IndicatorRegistry {
  /** id -> indicator (dedup by id, last-wins) */
  indicators: Map<string, DevToolsIndicator>;
  /** Cross-copy notification: each mounted consumer registers a callback here. */
  subscribers: Set<() => void>;
}

// Window-anchored singleton, mirroring the section registry — @abax/platform is
// not a Module Federation singleton, so every consumer evaluates its own copy of
// this module. The window anchor gives every copy one registry; falls back to a
// module-local registry where `window` is absent (SSR, Node test env).
const REGISTRY_KEY = '__ABAX_DEV_TOOLS_INDICATORS__';
let fallbackRegistry: IndicatorRegistry | undefined;

function createRegistry(): IndicatorRegistry {
  return { indicators: new Map(), subscribers: new Set() };
}

function getRegistry(): IndicatorRegistry {
  if (typeof window === 'undefined') {
    fallbackRegistry ??= createRegistry();
    return fallbackRegistry;
  }
  const w = window as unknown as Record<string, IndicatorRegistry | undefined>;
  w[REGISTRY_KEY] ??= createRegistry();
  return w[REGISTRY_KEY] as IndicatorRegistry;
}

// Reactivity bridge — module-local ref triggered via the shared window registry's
// subscribers Set so register calls from any @abax/platform copy notify all
// mounted consumers. See registry.ts for the pattern rationale.
const registryVersion = shallowRef(0);

/**
 * Contribute an indicator to the DevTools toggle pill. Dedupes by
 * {@link DevToolsIndicator.id} (last-wins) and notifies any mounted panel to
 * re-render.
 */
export function registerDevToolsIndicator(indicator: DevToolsIndicator): void {
  const registry = getRegistry();
  registry.indicators.set(indicator.id, indicator);
  registry.subscribers.forEach((cb) => cb());
}

/**
 * Reactive, sorted view of the registered indicators. Sorts by `order` ascending
 * (missing `order` sinks to the end) then by `id`. Recomputes whenever
 * {@link registerDevToolsIndicator} is called.
 */
export function useDevToolsIndicators(): ComputedRef<DevToolsIndicator[]> {
  const notify = () => {
    registryVersion.value += 1;
    triggerRef(registryVersion);
  };
  const registry = getRegistry();
  registry.subscribers.add(notify);
  onScopeDisposeOrWarn(() => registry.subscribers.delete(notify), 'useDevToolsIndicators()');

  return computed(() => {
    // Touch the version ref so register calls invalidate this computed.
    void registryVersion.value;
    return [...getRegistry().indicators.values()].sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      return a.id.localeCompare(b.id);
    });
  });
}

/** @internal Test/HMR helper — clears all registered indicators. */
export function __resetDevToolsIndicators(): void {
  const registry = getRegistry();
  registry.indicators.clear();
  registry.subscribers.forEach((cb) => cb());
}
